import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { API } from "@/lib/api";
import { BookOpen, Printer, CaretLeft, CaretRight, PiggyBank, TrendUp, Sparkle, ArrowUp, ArrowDown } from "@phosphor-icons/react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";

const money = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const money0 = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

function CategoryBars({ rows, color, testid }) {
  const max = Math.max(...rows.map((r) => r.amount), 1);
  return (
    <div className="space-y-3" data-testid={testid}>
      {rows.map((r) => (
        <div key={r.category}>
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="font-medium" style={{ color: "var(--bk-text)" }}>{r.category}</span>
            <span className="font-mono font-semibold" style={{ color: "var(--bk-text-2)" }}>{money(r.amount)}</span>
          </div>
          <div className="h-2 rounded-full" style={{ background: "var(--bk-page)" }}>
            <div className="h-2 rounded-full" style={{ width: `${Math.max(3, (r.amount / max) * 100)}%`, background: color }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function PublicClientReport() {
  const { shareId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const month = searchParams.get("month") || "";
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/public/client-reports/${shareId}${month ? `?month=${month}` : ""}`)
      .then((r) => {
        if (!r.ok) throw new Error("not found");
        return r.json();
      })
      .then((d) => { setReport(d); setError(""); })
      .catch(() => setError("This report link is invalid or has been revoked."))
      .finally(() => setLoading(false));
  }, [shareId, month]);

  if (error)
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "var(--bk-page)" }}>
        <div className="bk-card p-10 text-center max-w-md">
          <h1 className="font-display text-2xl font-bold mb-2" style={{ color: "var(--bk-text)" }}>Report unavailable</h1>
          <p style={{ color: "var(--bk-text-2)" }}>{error}</p>
        </div>
      </div>
    );

  if (!report)
    return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bk-page)" }}><span className="font-display text-2xl" style={{ color: "var(--bk-accent)" }}>Loading report…</span></div>;

  const { totals, savings } = report;
  const avail = report.available_months || [];
  const idx = avail.indexOf(report.month);
  const newer = idx > 0 ? avail[idx - 1] : null;
  const older = idx >= 0 && idx < avail.length - 1 ? avail[idx + 1] : null;
  const netUp = (totals.net_change ?? 0) >= 0;
  const hasSavings = savings.total_saved > 0 || savings.hikes_count > 0;

  return (
    <div className="min-h-screen" style={{ background: "var(--bk-page)", opacity: loading ? 0.6 : 1, transition: "opacity 0.15s ease" }}>
      <header className="border-b print:hidden" style={{ borderColor: "var(--bk-border)", background: "var(--bk-surface)" }}>
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: "var(--bk-accent)" }}>
              <BookOpen size={20} weight="duotone" color="#fff" />
            </span>
            <span className="font-display text-xl font-bold">IronRoot</span>
          </div>
          <button onClick={() => window.print()} data-testid="print-client-report" className="bk-btn-primary px-5 py-2 rounded-md text-sm font-semibold inline-flex items-center gap-2">
            <Printer size={18} weight="duotone" /> Print / Save PDF
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10" data-testid="public-client-report">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: "var(--bk-accent)" }}>Monthly Financial Report</span>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1" style={{ color: "var(--bk-text)" }} data-testid="client-report-business-name">{report.business_name}</h1>
            {report.industry && <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>{report.industry}</p>}
          </div>
          <div className="flex items-center gap-2 print:hidden">
            <button disabled={!older} onClick={() => older && setSearchParams({ month: older })} data-testid="client-report-prev-month" className="p-2 rounded-md border disabled:opacity-30" style={{ borderColor: "var(--bk-border)" }} aria-label="Previous month">
              <CaretLeft size={16} weight="bold" color="var(--bk-text)" />
            </button>
            <span className="font-display text-lg font-bold px-2" style={{ color: "var(--bk-text)" }} data-testid="client-report-month-label">{report.month_label}</span>
            <button disabled={!newer} onClick={() => newer && setSearchParams({ month: newer })} data-testid="client-report-next-month" className="p-2 rounded-md border disabled:opacity-30" style={{ borderColor: "var(--bk-border)" }} aria-label="Next month">
              <CaretRight size={16} weight="bold" color="var(--bk-text)" />
            </button>
          </div>
        </div>

        <div className="bk-card p-6 mb-8 flex items-start gap-3" style={{ background: "var(--bk-rowan-bg)", borderColor: "rgba(232,168,56,0.35)" }} data-testid="client-report-rowan-note">
          <Sparkle size={22} weight="duotone" color="var(--bk-accent)" className="shrink-0 mt-0.5" />
          <div>
            <p className="text-xs uppercase tracking-[0.18em] font-semibold mb-1" style={{ color: "var(--bk-accent)" }}>Highlights</p>
            <p className="text-sm leading-relaxed" style={{ color: "var(--bk-text)" }}>{report.rowan_note}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bk-card p-6" data-testid="client-report-income">
            <p className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Income</p>
            <p className="font-display text-3xl font-bold mt-2" style={{ color: "var(--bk-success)" }}>{money0(totals.income)}</p>
          </div>
          <div className="bk-card p-6" data-testid="client-report-expenses">
            <p className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Expenses</p>
            <p className="font-display text-3xl font-bold mt-2" style={{ color: "#B23B1F" }}>{money0(totals.expense)}</p>
          </div>
          <div className="bk-card p-6" data-testid="client-report-net">
            <p className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Net Profit</p>
            <p className="font-display text-3xl font-bold mt-2" style={{ color: totals.net >= 0 ? "var(--bk-text)" : "#B23B1F" }}>{money0(totals.net)}</p>
            {totals.net_change !== null && totals.net_change !== undefined && (
              <p className="text-xs font-semibold mt-1.5 inline-flex items-center gap-1" style={{ color: netUp ? "var(--bk-success)" : "#B23B1F" }} data-testid="client-report-net-change">
                {netUp ? <ArrowUp size={12} weight="bold" /> : <ArrowDown size={12} weight="bold" />}
                {money0(Math.abs(totals.net_change))} vs prior month
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bk-card p-6">
            <h3 className="font-display text-lg font-bold mb-4" style={{ color: "var(--bk-text)" }}>Where the money went</h3>
            {report.top_expense_categories.length ? (
              <CategoryBars rows={report.top_expense_categories} color="#B23B1F" testid="client-report-expense-categories" />
            ) : (
              <p className="text-sm" style={{ color: "var(--bk-text-2)" }}>No expenses recorded this month.</p>
            )}
          </div>
          <div className="bk-card p-6">
            <h3 className="font-display text-lg font-bold mb-4" style={{ color: "var(--bk-text)" }}>Where the money came from</h3>
            {report.top_income_categories.length ? (
              <CategoryBars rows={report.top_income_categories} color="var(--bk-success)" testid="client-report-income-categories" />
            ) : (
              <p className="text-sm" style={{ color: "var(--bk-text-2)" }}>No income recorded this month.</p>
            )}
          </div>
        </div>

        <div className="bk-card p-6 mb-8" data-testid="client-report-trend">
          <h3 className="font-display text-lg font-bold mb-4" style={{ color: "var(--bk-text)" }}>Six-month trend</h3>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={report.trend} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v >= 1000 ? `${Math.round(v / 1000)}k` : v}`} width={44} />
                <Tooltip formatter={(v, name) => [money(v), name === "income" ? "Income" : "Expenses"]} />
                <Legend formatter={(v) => (v === "income" ? "Income" : "Expenses")} iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="income" fill="#5B8A6F" radius={[4, 4, 0, 0]} />
                <Bar dataKey="expense" fill="#B23B1F" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {hasSavings && (
          <div className="bk-card p-6 mb-8" data-testid="client-report-savings">
            <h3 className="font-display text-lg font-bold mb-4" style={{ color: "var(--bk-text)" }}>Savings wins</h3>
            <div className="flex flex-wrap gap-4">
              {savings.total_saved > 0 && (
                <div className="rounded-2xl border px-5 py-3" style={{ borderColor: "rgba(91,138,111,0.3)", background: "var(--bk-success-bg)" }}>
                  <p className="text-[10px] uppercase tracking-[0.14em] font-semibold inline-flex items-center gap-1" style={{ color: "var(--bk-success)" }}><PiggyBank size={12} weight="bold" /> Saved from cancelled subscriptions</p>
                  <p className="font-display text-2xl font-bold" style={{ color: "var(--bk-success)" }}>{money(savings.total_saved)}</p>
                </div>
              )}
              {savings.hikes_count > 0 && (
                <div className="rounded-2xl border px-5 py-3" style={{ borderColor: "rgba(232,168,56,0.35)", background: "#FFF4D9" }}>
                  <p className="text-[10px] uppercase tracking-[0.14em] font-semibold inline-flex items-center gap-1" style={{ color: "#8A6A3B" }}><TrendUp size={12} weight="bold" /> Price hikes caught</p>
                  <p className="font-display text-2xl font-bold" style={{ color: "#8A6A3B" }}>{savings.hikes_count} · {money(savings.hikes_extra_monthly)}/mo</p>
                </div>
              )}
            </div>
          </div>
        )}

        <p className="text-xs text-center pb-4" style={{ color: "var(--bk-disabled)" }}>
          Prepared with IronRoot · Generated {new Date(report.generated_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })} · Totals and top categories shown; full ledger detail stays private.
        </p>
      </main>
    </div>
  );
}
